import { useMemo, useState } from 'react'
import {
  applyMapping,
  parseTableText,
  suggestColumnMapping,
} from '@/utils/tableImport'

/**
 * Paste or upload CSV/TSV, map columns to this table's fields, preview, import.
 */
export function TableImportPanel({ columns, onCancel, onImport, importing }) {
  const [rawText, setRawText] = useState('')
  const [parseError, setParseError] = useState('')
  const [headers, setHeaders] = useState([])
  const [dataRows, setDataRows] = useState([])
  const [mapping, setMapping] = useState({})
  const [step, setStep] = useState('input') // input | preview

  const mappedCount = useMemo(
    () => columns.filter((c) => mapping[c.key] != null && mapping[c.key] >= 0).length,
    [columns, mapping],
  )

  const previewRows = useMemo(() => {
    if (!dataRows.length) return []
    return applyMapping(headers, dataRows, columns, mapping).slice(0, 8)
  }, [headers, dataRows, columns, mapping])

  function runParse(text) {
    const result = parseTableText(text)
    if (result.error) {
      setParseError(result.error)
      setHeaders([])
      setDataRows([])
      setMapping({})
      setStep('input')
      return
    }
    setParseError('')
    setHeaders(result.headers)
    setDataRows(result.rows)
    setMapping(suggestColumnMapping(result.headers, columns))
    setStep('preview')
  }

  function handleFile(file) {
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const text = String(reader.result || '')
      setRawText(text)
      runParse(text)
    }
    reader.onerror = () => setParseError('Could not read that file.')
    reader.readAsText(file)
  }

  async function confirmImport() {
    const rows = applyMapping(headers, dataRows, columns, mapping).filter((row) =>
      Object.values(row.values).some((v) => String(v || '').trim()),
    )
    if (!rows.length) {
      setParseError('Nothing to import after mapping — check column matches.')
      return
    }
    await onImport(rows)
  }

  return (
    <div className="rounded-lg border border-slate-300 bg-slate-50 p-3 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-brand">Import rows</h3>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-brand-muted underline"
          disabled={importing}
        >
          Close
        </button>
      </div>
      <p className="mt-1 text-xs text-brand-muted">
        Paste a table from Excel or Word, or choose a CSV / TSV file. The first row must be
        column headers. Matching is by header name — tweak the map if needed, then import.
      </p>

      {step === 'input' && (
        <div className="mt-3 space-y-3">
          <label className="block text-xs font-medium text-brand">
            Paste table
            <textarea
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              rows={8}
              placeholder={'Name\tLocal Network ID\tMS 365 Password\nAlice Smith\talice.smith\tExample-pass'}
              className="mt-1 w-full rounded border border-slate-300 bg-white px-2.5 py-2 font-mono text-xs text-brand outline-none focus:border-brand focus:ring-1 focus:ring-brand"
              disabled={importing}
            />
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => runParse(rawText)}
              disabled={importing || !rawText.trim()}
              className="rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:brightness-110 disabled:opacity-50"
            >
              Preview import
            </button>
            <label className="cursor-pointer text-xs font-medium text-brand underline">
              Or choose CSV / TSV file
              <input
                type="file"
                accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
                className="sr-only"
                disabled={importing}
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </label>
          </div>
        </div>
      )}

      {step === 'preview' && (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-brand-muted">
            {dataRows.length} data row{dataRows.length === 1 ? '' : 's'} found · {mappedCount} of{' '}
            {columns.length} fields mapped
          </p>

          <div className="space-y-2 rounded border border-slate-200 bg-white p-2">
            <p className="text-xs font-semibold text-brand">Column map</p>
            {columns.map((col) => (
              <label
                key={col.key}
                className="flex flex-col gap-0.5 text-xs sm:flex-row sm:items-center sm:gap-3"
              >
                <span className="min-w-[10rem] font-medium text-brand">{col.label}</span>
                <select
                  value={mapping[col.key] ?? ''}
                  onChange={(e) => {
                    const v = e.target.value
                    setMapping((prev) => ({
                      ...prev,
                      [col.key]: v === '' ? null : Number(v),
                    }))
                  }}
                  className="rounded border border-slate-300 bg-white px-2 py-1 text-xs"
                  disabled={importing}
                >
                  <option value="">— skip —</option>
                  {headers.map((h, idx) => (
                    <option key={`${idx}-${h}`} value={idx}>
                      {h || `(column ${idx + 1})`}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>

          <div className="overflow-x-auto rounded border border-slate-200 bg-white">
            <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
              <thead className="bg-slate-100">
                <tr>
                  {columns.map((col) => (
                    <th key={col.key} className="whitespace-nowrap px-2 py-1.5 font-semibold text-brand-muted">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {previewRows.map((row, i) => (
                  <tr key={i}>
                    {columns.map((col) => (
                      <td key={col.key} className="max-w-[12rem] truncate px-2 py-1 text-brand">
                        {row.values[col.key] || (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {dataRows.length > previewRows.length && (
              <p className="border-t border-slate-100 px-2 py-1.5 text-xs text-brand-muted">
                Showing first {previewRows.length} of {dataRows.length} rows
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={confirmImport}
              disabled={importing || mappedCount === 0}
              className="rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:brightness-110 disabled:opacity-50"
            >
              {importing
                ? 'Importing…'
                : `Import ${dataRows.length} row${dataRows.length === 1 ? '' : 's'}`}
            </button>
            <button
              type="button"
              onClick={() => setStep('input')}
              disabled={importing}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-brand hover:bg-slate-50"
            >
              Back
            </button>
          </div>
        </div>
      )}

      {parseError && (
        <p className="mt-2 text-xs text-red-700" role="alert">
          {parseError}
        </p>
      )}
    </div>
  )
}
